import { FilterListIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { noop } from '@synergy/utils';

import { FilterContext, useFilter } from './context/filter-context';
import { PoolButton } from '../button/pool-button';
import { PoolModal } from '../modal/pool-modal';

const useStyles = makeStyles((theme: Theme) => ({
    filterButtonActive: {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',

        '&:hover': {
            backgroundColor: theme.palette.grey[400],
        },
    },
    filterText: {
        paddingLeft: 8,
    },
    iconFontSize: {
        fontSize: 20,
    },
    ctaButton: {
        minWidth: 148,
    },
    contextWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2, 0),
    },
}));

interface FilterModalProps {
    title?: ReactNode;
}

export const FilterModal = (props: PropsWithChildren<FilterModalProps>) => {
    const { filter, reset, updateFilter, ...parentFilterContext } = useFilter();
    const [nextFilter, setNextFilter] = useState<URLSearchParams>(new URLSearchParams());

    useEffect(() => {
        setNextFilter(filter);
    }, [setNextFilter, filter]);

    const [isOpen, setIsOpen] = useState(false);
    const classes = useStyles();

    const onCommit = () => {
        updateFilter(nextFilter);
        setIsOpen(false);
    };

    const onReset = () => {
        reset();
        setIsOpen(false);
    };

    const actionChildren = [
        <PoolButton key="1" variant="text" styleType="firstLevel" onClick={onReset} className={classes.ctaButton}>
            <FormattedMessage id="app.common.reset" />
        </PoolButton>,
        <PoolButton key="2" styleType="firstLevel" onClick={onCommit} className={classes.ctaButton}>
            <FormattedMessage id="app.common.filter" />
        </PoolButton>,
    ];

    const hasActiveFilter = Object.values(filter).filter(value => value !== undefined && value !== null).length > 0;
    const filterButtonClasses = hasActiveFilter ? ` ${classes.filterButtonActive}` : '';

    return (
        <>
            <PoolButton
                styleType="filter"
                className={filterButtonClasses}
                onClick={() => setIsOpen(true)}
                sx={{ color: theme => theme.palette.text.primary }}
            >
                <FilterListIcon className={classes.iconFontSize} />
                <span className={classes.filterText}>
                    <FormattedMessage id="app.common.filter" />
                </span>
            </PoolButton>
            <PoolModal
                isVisible={isOpen}
                title={props.title ?? <FormattedMessage id="app.filter.filterTitle" />}
                handleClose={() => setIsOpen(false)}
                actionChildren={actionChildren}
            >
                <FilterContext.Provider
                    value={{
                        ...parentFilterContext,
                        filter: nextFilter,
                        reset: noop,
                        updateFilter: filter => setNextFilter(new URLSearchParams(filter)),
                    }}
                >
                    <div className={classes.contextWrapper}>{props.children}</div>
                </FilterContext.Provider>
            </PoolModal>
        </>
    );
};
